import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Search, Check, ChevronDown } from 'lucide-react';
import type { Artist } from '@/domain/types';

interface Props {
  artists: Artist[];
  /** Currently-selected artist id (single-select). Leave undefined for the collab "add" use. */
  value?: string;
  /** Ids to hide from the list (e.g. the primary artist + already-added collabs). */
  excludeIds?: string[];
  onSelect: (artistId: string) => void;
  /** Trigger text when nothing is selected. */
  placeholder?: string;
  /** Overrides the trigger text entirely (e.g. "+ Add collab artist"); stays fixed after a pick. */
  triggerLabel?: string;
  testid: string;
  /** Renders a subtle "required" ring when nothing is selected. */
  required?: boolean;
}

/**
 * Searchable single-select artist picker. Used for the required PRIMARY ARTIST
 * field and — with `triggerLabel` — as the "+ Add collab artist" affordance
 * (the same picker, filtered to exclude the primary + existing collabs).
 */
export default function ArtistPicker({
  artists,
  value,
  excludeIds = [],
  onSelect,
  placeholder = 'Select artist…',
  triggerLabel,
  testid,
  required,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const excluded = useMemo(() => new Set(excludeIds), [excludeIds]);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return artists
      .filter((a) => !excluded.has(a.id))
      .filter((a) => (q ? a.displayName.toLowerCase().includes(q) : true))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [artists, excluded, query]);

  const selected = value ? artists.find((a) => a.id === value) : undefined;
  const label = triggerLabel ?? selected?.displayName ?? placeholder;
  const empty = !triggerLabel && !selected;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          setQuery('');
        }}
        className={clsx(
          'w-full flex items-center justify-between bg-ink-black border rounded-md px-3 py-2 text-left text-sm transition',
          empty && required
            ? 'border-gold-bright/50 text-cream-faint'
            : 'border-gold-dim/40 hover:border-gold-bright/60',
          selected ? 'text-cream' : 'text-cream-faint',
        )}
        data-testid={testid}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{label}</span>
        <ChevronDown size={13} className="text-cream-faint shrink-0" />
      </button>

      {open ? (
        <div
          className="absolute z-20 mt-1 w-full panel-dark py-1 shadow-ink-soft"
          data-testid={`${testid}-menu`}
        >
          <div className="relative px-2 pb-1">
            <Search size={12} className="absolute left-4 top-1/2 -translate-y-1/2 text-cream-faint" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search artist"
              className="w-full bg-ink-black border border-gold-dim/40 rounded-md pl-7 pr-2 py-1.5 text-sm text-cream focus:outline-none focus:border-gold-bright"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto">
            {results.length === 0 ? (
              <li className="px-3 py-2 font-serif text-sm text-cream-soft">No artists.</li>
            ) : null}
            {results.map((a) => {
              const active = a.id === value;
              return (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(a.id);
                      setOpen(false);
                    }}
                    className={clsx(
                      'w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-ink-soft/60 transition',
                      active ? 'text-gold-bright' : 'text-cream',
                    )}
                    data-testid={`${testid}-option-${a.id}`}
                  >
                    {a.displayName}
                    {active ? <Check size={12} /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
